import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { BarChart3, GitBranch, LineChart as LineChartIcon, Table2, UserRound } from 'lucide-react'
import Panel from '@/components/dashboards/Panel'
import { BasisNote } from '@/components/dashboards/KnownFigures'
import {
  CHART_MEASURES,
  DIMENSION_LABEL,
  MEASURE_BY_KEY,
  ORDERED_DIMENSIONS,
  ROLE_DIMENSION,
  describeFilters,
  toggleFilterValue,
  totalsOf,
  type AnalyticsDimension,
  type AnalyticsFilters,
  type DealFact,
  type FlowDateField,
  type PeopleRole,
} from '@/services/dashboardAnalytics'
import { useAnalyticsDimensions, useDealFacts, useFlow, useFunnel, usePivot } from './analyticsQueries'
import CohortBar from './CohortBar'
import { useCohort, useExplorerState } from './explorerState'
import FlowChart from './FlowChart'
import FunnelStrip from './FunnelStrip'
import PivotChart from './PivotChart'
import PivotTable, { DEFAULT_PIVOT_COLUMNS, emptyMeasureColumns } from './PivotTable'

/** Dimensions that name a person or a firm — their rows link to a profile. */
const PROFILE_ROLE: Partial<Record<string, PeopleRole>> = Object.fromEntries(
  (Object.entries(ROLE_DIMENSION) as Array<[PeopleRole, AnalyticsDimension]>).map(([role, dimension]) => [
    dimension,
    role,
  ]),
)

const FLOW_LABEL: Record<FlowDateField, string> = {
  event: 'Each event date',
  received: 'Received date',
  funded: 'Funded date',
  status: 'Status date',
}

export function profileHref(role: PeopleRole, name: string): string {
  return `/dashboards/people?view=person&role=${encodeURIComponent(role)}&name=${encodeURIComponent(name)}`
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: Array<{ value: string; label: string }>
}) {
  return (
    <label className="flex items-center gap-1.5">
      <span className="font-mono text-[8.5px] uppercase tracking-[0.18em] text-stone-500">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded border border-amber-400/15 bg-[#090909] px-1.5 py-1 text-[11px] text-stone-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  )
}

/**
 * Pipeline Explorer: any dimension, optionally split by a second, any
 * stackable measure — over the board's cohort. A bar or a row label narrows
 * the cohort (chips above remove it again); every number in the table, the
 * funnel and the flow opens exactly its deals. All of it lives in the URL, so
 * a view can be sent to someone and opens the same.
 */
export default function PipelineExplorer({
  className,
  showCohortBar = true,
}: {
  className?: string
  /** Off when the host board already shows the cohort bar above its tabs. */
  showCohortBar?: boolean
}) {
  const [state, update] = useExplorerState()
  const [cohort, setCohort] = useCohort()
  const dimensions = useAnalyticsDimensions()

  const dimensionOptions = useMemo(() => {
    const served = dimensions.data?.dimensions
    const list = served?.length
      ? served
      : (Object.keys(DIMENSION_LABEL) as AnalyticsDimension[]).map((key) => ({ key, label: DIMENSION_LABEL[key] }))
    return list.map((entry) => ({ value: entry.key, label: entry.label }))
  }, [dimensions.data])
  const labelFor = (key: string) =>
    dimensionOptions.find((option) => option.value === key)?.label ??
    DIMENSION_LABEL[key as AnalyticsDimension] ??
    key

  const pivot = usePivot({ dimension: state.dimension, by: state.by, filters: cohort })
  const funnel = useFunnel(cohort)
  const flow = useFlow(cohort, state.flowField)
  const facts = useDealFacts(cohort)

  const factMap = useMemo(() => {
    const map = new Map<string, DealFact>()
    for (const fact of facts.data?.deals ?? []) map.set(fact.id, fact)
    return map
  }, [facts.data])

  const rows = pivot.data?.rows ?? []
  const totals = totalsOf(pivot.data)
  const ordered = ORDERED_DIMENSIONS.has(state.dimension)
  const scopeLabel = describeFilters(cohort, labelFor)
  const role = PROFILE_ROLE[state.dimension]

  const narrow = (key: string | null) => setCohort(toggleFilterValue(cohort, state.dimension, key))
  const narrowSegment = (rowKey: string | null, childKeys: Array<string | null>) => {
    let next: AnalyticsFilters = toggleFilterValue(cohort, state.dimension, rowKey)
    if (state.by && childKeys.length === 1) next = toggleFilterValue(next, state.by, childKeys[0])
    setCohort(next)
  }

  const byOptions = [
    { value: 'none', label: 'No split' },
    ...dimensionOptions.filter((option) => option.value !== state.dimension),
  ]

  const unrecorded = rows.find((row) => row.key == null)
  const emptyColumns = emptyMeasureColumns(rows, totals, DEFAULT_PIVOT_COLUMNS)

  return (
    <div className={clsx('flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto scrollbar-thin pr-0.5', className)}>
      {showCohortBar && <CohortBar cohort={cohort} onChange={setCohort} className="flex-shrink-0" />}

      <div className="flex flex-shrink-0 flex-wrap items-center gap-x-4 gap-y-1.5 rounded-md border border-amber-400/[0.08] bg-[#0a0a0a] px-2.5 py-1.5">
        <Select label="Group by" value={state.dimension} onChange={(dimension) => update({ dimension })} options={dimensionOptions} />
        <Select
          label="Split by"
          value={state.by ?? 'none'}
          onChange={(value) => update({ by: value === 'none' ? null : value })}
          options={byOptions}
        />
        <div className="flex items-center gap-1.5" role="radiogroup" aria-label="Measure">
          <span className="font-mono text-[8.5px] uppercase tracking-[0.18em] text-stone-500">Measure</span>
          <div className="flex items-center gap-0.5 rounded border border-amber-400/10 p-0.5">
            {CHART_MEASURES.map((measure) => (
              <button
                key={measure}
                type="button"
                role="radio"
                aria-checked={state.measure === measure}
                onClick={() => update({ measure })}
                className={clsx(
                  'rounded px-2 py-[3px] text-[10.5px] transition-colors',
                  state.measure === measure ? 'bg-amber-400/[0.12] text-amber-100' : 'text-stone-500 hover:text-stone-200',
                )}
              >
                {MEASURE_BY_KEY[measure].label}
              </button>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={() => update({ by: state.by === 'outcome' ? null : 'outcome' })}
          aria-pressed={state.by === 'outcome'}
          className={clsx(
            'rounded-full border px-2 py-[3px] text-[10px] transition-colors',
            state.by === 'outcome'
              ? 'border-amber-400/35 bg-amber-400/[0.08] text-amber-200'
              : 'border-amber-400/10 text-stone-500 hover:text-stone-300',
          )}
        >
          Stack by outcome
        </button>
        {role && (
          <span className="ml-auto flex items-center gap-1 text-[10px] text-stone-500">
            <UserRound size={10} aria-hidden="true" /> Each {labelFor(state.dimension).toLowerCase()} opens a profile
          </span>
        )}
      </div>

      <Panel
        title="Pipeline funnel"
        kicker={scopeLabel}
        isLoading={funnel.isLoading}
        error={funnel.error}
        onRetry={() => void funnel.refetch()}
        className="flex-shrink-0"
        bodyClassName="!py-2"
        footer={
          <BasisNote full={funnel.data?.basis}>
            Deals in each stage now; the arrow is the share that reached the next stage; dwell in 24-hour days.
          </BasisNote>
        }
        skeletonRows={2}
      >
        <FunnelStrip data={funnel.data} scopeLabel={scopeLabel} />
      </Panel>

      <div className="grid flex-shrink-0 grid-cols-1 gap-2 xl:grid-cols-5">
        <Panel
          title={`${MEASURE_BY_KEY[state.measure].label} by ${labelFor(state.dimension).toLowerCase()}${state.by ? ` · split by ${labelFor(state.by).toLowerCase()}` : ''}`}
          kicker={
            pivot.data
              ? `${rows.length.toLocaleString('en-US')} groups${!ordered && rows.length > 16 ? ' · top 16 drawn' : ''}`
              : undefined
          }
          isLoading={pivot.isLoading}
          error={pivot.error}
          onRetry={() => void pivot.refetch()}
          isEmpty={!pivot.isLoading && rows.length === 0}
          emptyIcon={BarChart3}
          emptyTitle="No deal in this cohort"
          emptyDescription="Remove a filter chip above to widen the cohort."
          className="h-[340px] xl:col-span-3"
          bodyClassName="flex min-h-0 flex-col !pb-1"
          footer={
            unrecorded
              ? `${unrecorded.measures.count} deal${unrecorded.measures.count === 1 ? '' : 's'} record no ${labelFor(state.dimension).toLowerCase()} — grouped as "${unrecorded.label || 'Not recorded'}". Click a bar to narrow the cohort.`
              : 'Click a bar to narrow the cohort to it.'
          }
        >
          <PivotChart
            rows={rows}
            by={state.by}
            measure={state.measure}
            ordered={ordered}
            onSelectRow={narrow}
            onSelectSegment={narrowSegment}
          />
        </Panel>

        <Panel
          title="Monthly flow"
          kicker={
            <span className="flex items-center gap-1">
              <LineChartIcon size={10} aria-hidden="true" />
              <select
                aria-label="Month of"
                value={state.flowField}
                onChange={(event) => update({ flowField: event.target.value as FlowDateField })}
                className="rounded border border-amber-400/15 bg-[#090909] px-1 py-0.5 text-[10px] text-stone-300 focus:outline-none"
              >
                {(Object.keys(FLOW_LABEL) as FlowDateField[]).map((field) => (
                  <option key={field} value={field}>
                    By {FLOW_LABEL[field].toLowerCase()}
                  </option>
                ))}
              </select>
            </span>
          }
          isLoading={flow.isLoading}
          error={flow.error}
          onRetry={() => void flow.refetch()}
          className="h-[340px] xl:col-span-2"
          bodyClassName="flex min-h-0 flex-col !pb-1"
        >
          <FlowChart data={flow.data} scopeLabel={scopeLabel} />
        </Panel>
      </div>

      <Panel
        title={`Every ${labelFor(state.dimension).toLowerCase()} · every measure`}
        kicker={
          <span className="flex items-center gap-1">
            <Table2 size={10} aria-hidden="true" />
            {facts.isLoading ? 'Resolving exact drills…' : 'Each number opens exactly its deals'}
          </span>
        }
        isLoading={pivot.isLoading}
        error={pivot.error}
        onRetry={() => void pivot.refetch()}
        isEmpty={!pivot.isLoading && rows.length === 0}
        emptyIcon={GitBranch}
        className="min-h-[260px] flex-shrink-0"
        bodyClassName="!p-0"
        footer={[
          'Commitment = Total Loan Amount (Fully Funded). Funded = funded amount, else commitment, on funded deals.',
          'Quote rate = deals at Post-screen Quote or later ÷ deals submitted (T-71). Funded rate = funded ÷ all.',
          'Weighted LTV/LTC is commitment-weighted, in-place LTV else LTC; coverage under each value.',
          emptyColumns.length
            ? `Not shown, no value on any row: ${emptyColumns.map((column) => MEASURE_BY_KEY[column.key].label).join(', ')}.`
            : null,
          pivot.data?.basis,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <PivotTable
          hideEmptyNote
          rows={rows}
          totals={totals}
          dimensionLabel={labelFor(state.dimension)}
          by={state.by}
          facts={factMap}
          scopeLabel={scopeLabel}
          onSelectRow={narrow}
          ordered={ordered}
          rowAction={
            role
              ? (row) =>
                  row.key ? (
                    <Link
                      to={profileHref(role, row.key)}
                      className="inline-flex items-center gap-0.5 rounded border border-amber-400/20 px-1.5 py-0.5 text-[9.5px] text-amber-200 hover:bg-amber-400/10"
                    >
                      <UserRound size={9} aria-hidden="true" /> Profile
                    </Link>
                  ) : null
              : undefined
          }
        />
      </Panel>
    </div>
  )
}
